/* eslint-disable max-len */
import { Icon, IconButton, makeStyles } from '@material-ui/core';
import { CSSProperties, useCallback, useContext, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { ritzDataContext, selectSudoProjectAccessKey } from '@moderepo/mode-data-state-ritz';
import { EntityUIComponentCollection } from '..';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';


const useStyle = makeStyles(() => {
    return {
        root: {
            height  : '100%',
            width   : '100%',
            position: 'relative',
        },

        iframe: {
            height: '100%',
            width : '100%',
            border: 'none',
        },

        fullscreenButton: {
            position  : 'absolute',
            top       : 0,
            right     : 0,
            background: 'rgba(0, 0, 0, 0.4)',
        },
    };
}, { name: 'EntityIFrameUIComponent', index: 1 });

const API_KEY_PARAM = '#apiKey=';


/**
 * This is the interface for the EntityIFrameUIComponent's CustomSettings.
 */
export interface EntityIFrameUIComponentCustomSettings {
    readonly srcUrl: string;

    // Whether or not to append the project's API key to the srcUrl. The default is false.
    readonly passApiKey?: boolean;

    // To optionally add a button for the user to view the component in fullscreen. The default is FALSE.
    readonly allowFullscreen?: boolean;

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // The custom CSS style for the fullscreen button
    readonly fullscreenButtonStyle?: CSSProperties | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntityIFrameUIComponentCustomSettings
 */
export const isEntityIFrameUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityIFrameUIComponentCustomSettings => {
    const settings = obj as EntityIFrameUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.srcUrl, true, false)) {
        return errorLogger?.logError({
            message: '"srcUrl" is required and must be a non-empty string.',
            value  : settings.srcUrl,
            path   : [...configPath, 'srcUrl'],
        }) ?? false;
    }

    // Make sure the URL is valid
    try {
        const url = new URL(settings.srcUrl);
    } catch (error) {
        return errorLogger?.logError({
            message: '"srcUrl" is invalid.',
            value  : settings.srcUrl,
            path   : [...configPath, 'srcUrl'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.passApiKey, false)) {
        return errorLogger?.logError({
            message: '"passApiKey" must be either undefined or a boolean.',
            value  : settings.passApiKey,
            path   : [...configPath, 'passApiKey'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.allowFullscreen, false)) {
        return errorLogger?.logError({
            message: '"allowFullscreen" must be either undefined or a boolean.',
            value  : settings.allowFullscreen,
            path   : [...configPath, 'allowFullscreen'],
        }) ?? false;
    }


    // "containerStyle" is optional but it provided, it must be an object
    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    // "fullscreenButtonStyle" is optional but it provided, it must be an object
    if (settings.fullscreenButtonStyle !== undefined && !isObjectLiteral(settings.fullscreenButtonStyle)) {
        return errorLogger?.logError({
            message: '"fullscreenButtonStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'fullscreenButtonStyle'],
        }) ?? false;
    }

    return true;
};


export interface EntityIFrameUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityIFrameUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for creating a
 */
export const EntityIFrameUIComponent: React.FC<EntityIFrameUIComponentProps> = (props: EntityIFrameUIComponentProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);

    const classes = useStyle();
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const iframeSrc = useMemo(() => {
        if (props.customSettings?.passApiKey) {
            return `${props.customSettings?.srcUrl}${API_KEY_PARAM}${projectKey?.token}`;
        }
        return props.customSettings?.srcUrl;
    }, [projectKey?.token, props.customSettings?.passApiKey, props.customSettings?.srcUrl]);

    const onFullscreenButtonClick = useCallback(() => {
        if (iframeRef.current instanceof HTMLIFrameElement) {
            iframeRef.current.requestFullscreen();
        }
    }, []);

    return (
        <div className={classes.root}>
            <iframe
                ref={iframeRef}
                title={props.customSettings?.srcUrl}
                src={iframeSrc}
                className={clsx(props.className, classes.iframe)}
                style={{ ...props.style, ...props.customSettings?.containerStyle }}
            />
            {props.customSettings?.allowFullscreen && (
                <IconButton
                    className={classes.fullscreenButton}
                    style={props.customSettings?.fullscreenButtonStyle}
                    onClick={onFullscreenButtonClick}
                >
                    <Icon>fullscreen</Icon>
                </IconButton>
            )}
        </div>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityIFrameUIComponent', EntityIFrameUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_IFrame_Comp',
    {
        componentFunc          : EntityIFrameUIComponent,
        customSettingsValidator: isEntityIFrameUIComponentCustomSettings,
    },
);
